import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../../utils/api';
import { FaUsers, FaCreditCard, FaCheckCircle, FaClock, FaChartLine, FaSync } from 'react-icons/fa';
import { toast } from 'react-toastify';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rotatingQR, setRotatingQR] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await adminAPI.getDashboard();
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleRotateQR = async () => {
    if (!window.confirm('Are you sure you want to rotate the QR code?')) return;

    setRotatingQR(true);
    try {
      await adminAPI.rotateQRCode();
      toast.success('QR code rotated successfully');
    } catch (error) {
      console.error('Error rotating QR:', error);
      toast.error('Failed to rotate QR code');
    } finally {
      setRotatingQR(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-secondary-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-secondary-900">Admin Dashboard</h1>
            <p className="text-secondary-600 mt-2">Manage students and payments</p>
          </div>
          <button
            onClick={handleRotateQR}
            disabled={rotatingQR}
            className="flex items-center gap-2 bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            <FaSync className={rotatingQR ? 'animate-spin' : ''} />
            {rotatingQR ? 'Rotating...' : 'Rotate QR Code'}
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-blue-100 p-3 rounded-lg">
                <FaUsers className="text-blue-600" size={24} />
              </div>
              <FaChartLine className="text-green-500" size={20} />
            </div>
            <h3 className="text-secondary-600 text-sm font-medium">Total Students</h3>
            <p className="text-3xl font-bold text-secondary-900 mt-2">
              {stats?.totalStudents || 0}
            </p>
            <p className="text-xs text-secondary-500 mt-2">
              +{stats?.registrations.today || 0} today
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-yellow-100 p-3 rounded-lg">
                <FaClock className="text-yellow-600" size={24} />
              </div>
            </div>
            <h3 className="text-secondary-600 text-sm font-medium">Pending Verifications</h3>
            <p className="text-3xl font-bold text-yellow-600 mt-2">
              {stats?.pendingVerifications || 0}
            </p>
            <Link to="/admin/students?status=submitted" className="text-xs text-primary-600 hover:text-primary-700 mt-2 inline-block">
              View all →
            </Link>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-green-100 p-3 rounded-lg">
                <FaCheckCircle className="text-green-600" size={24} />
              </div>
            </div>
            <h3 className="text-secondary-600 text-sm font-medium">Verified Payments</h3>
            <p className="text-3xl font-bold text-green-600 mt-2">
              {stats?.payments.verified || 0}
            </p>
            <p className="text-xs text-secondary-500 mt-2">
              Total: {stats?.payments.submitted + stats?.payments.verified || 0}
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-purple-100 p-3 rounded-lg">
                <FaCreditCard className="text-purple-600" size={24} />
              </div>
            </div>
            <h3 className="text-secondary-600 text-sm font-medium">Total Revenue</h3>
            <p className="text-3xl font-bold text-purple-600 mt-2">
              ₹{(stats?.revenue.total || 0).toLocaleString()}
            </p>
            <p className="text-xs text-secondary-500 mt-2">
              From {stats?.revenue.verified || 0} payments
            </p>
          </div>
        </div>

        {/* Registrations Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Today's Registrations</h3>
            <p className="text-4xl font-bold text-primary-600">{stats?.registrations.today || 0}</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">This Week</h3>
            <p className="text-4xl font-bold text-primary-600">{stats?.registrations.weekly || 0}</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">This Month</h3>
            <p className="text-4xl font-bold text-primary-600">{stats?.registrations.monthly || 0}</p>
          </div>
        </div>

        {/* Payment Status Overview */}
        <div className="bg-white p-6 rounded-xl shadow-md mb-8">
          <h2 className="text-xl font-bold text-secondary-900 mb-6">Payment Status Overview</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <p className="text-2xl font-bold text-yellow-600">{stats?.payments.pending || 0}</p>
              <p className="text-sm text-secondary-600 mt-1">Pending</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{stats?.payments.submitted || 0}</p>
              <p className="text-sm text-secondary-600 mt-1">Submitted</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{stats?.payments.verified || 0}</p>
              <p className="text-sm text-secondary-600 mt-1">Verified</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-2xl font-bold text-red-600">{stats?.payments.rejected || 0}</p>
              <p className="text-sm text-secondary-600 mt-1">Rejected</p>
            </div>
          </div>
        </div>

        {/* Application Status Overview */}
        <div className="bg-white p-6 rounded-xl shadow-md mb-8">
          <h2 className="text-xl font-bold text-secondary-900 mb-6">Application Status Overview</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-secondary-50 rounded-lg">
              <p className="text-2xl font-bold text-secondary-600">{stats?.applications.incomplete || 0}</p>
              <p className="text-sm text-secondary-600 mt-1">Incomplete</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <p className="text-2xl font-bold text-yellow-600">{stats?.applications.pending || 0}</p>
              <p className="text-sm text-secondary-600 mt-1">Pending</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{stats?.applications.approved || 0}</p>
              <p className="text-sm text-secondary-600 mt-1">Approved</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-2xl font-bold text-red-600">{stats?.applications.rejected || 0}</p>
              <p className="text-sm text-secondary-600 mt-1">Rejected</p>
            </div>
          </div>
        </div>

        {/* Recent Students */}
        <div className="bg-white p-6 rounded-xl shadow-md">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-secondary-900">Recent Registrations</h2>
            <Link to="/admin/students" className="text-primary-600 hover:text-primary-700 font-medium">
              View All →
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-600 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-600 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-600 uppercase">College</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-600 uppercase">Payment</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-600 uppercase">Application</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-600 uppercase">Registered</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-200">
                {stats?.recentStudents && stats.recentStudents.length > 0 ? (
                  stats.recentStudents.map((student) => (
                    <tr key={student._id} className="hover:bg-secondary-50">
                      <td className="px-4 py-3 text-sm font-medium text-secondary-900">{student.name}</td>
                      <td className="px-4 py-3 text-sm text-secondary-600">{student.email}</td>
                      <td className="px-4 py-3 text-sm text-secondary-600">{student.collegeName}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          student.paymentStatus === 'verified' ? 'bg-green-100 text-green-800' :
                          student.paymentStatus === 'submitted' ? 'bg-blue-100 text-blue-800' :
                          student.paymentStatus === 'rejected' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {student.paymentStatus}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          student.applicationStatus === 'approved' ? 'bg-green-100 text-green-800' :
                          student.applicationStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          student.applicationStatus === 'rejected' ? 'bg-red-100 text-red-800' :
                          'bg-secondary-100 text-secondary-800'
                        }`}>
                          {student.applicationStatus}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-secondary-600">
                        {new Date(student.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-secondary-500">
                      No recent registrations
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
